import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ContentCopy,
  Delete,
  Edit,
  OpenInNew,
  PhotoCamera,
  Storefront,
  Save,
} from "@mui/icons-material";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";

const LojaConfig = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  const [categories, setCategories] = useState([]);
  const [catModal, setCatModal] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", order: 0 });
  const [catEditing, setCatEditing] = useState(null);
  const [catDeleting, setCatDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/store-settings"),
        api.get("/product-categories"),
      ]);
      setSettings(s.data);
      setCategories(c.data || []);
      if (s.data?.bannerUrl) {
        setBannerPreview(
          `${getBackendUrl()}/public/company${s.data.companyId}/store/${s.data.bannerUrl}`
        );
      }
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSettings((s) => ({ ...s, [field]: value }));
  };

  const lojaUrl = settings ? `${window.location.origin}/loja/${settings.slug}` : "";

  const copiar = () => {
    navigator.clipboard
      .writeText(lojaUrl)
      .then(() => toast.success("Link copiado!"))
      .catch(() => toast.error("Não foi possível copiar."));
  };

  const pickBanner = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const salvar = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/store-settings", {
        slug: settings.slug,
        enabled: settings.enabled,
        title: settings.title,
        description: settings.description,
        primaryColor: settings.primaryColor,
        isOpen: settings.isOpen,
        deliveryEnabled: settings.deliveryEnabled,
        instagramUrl: settings.instagramUrl,
        facebookUrl: settings.facebookUrl,
        youtubeUrl: settings.youtubeUrl,
      });

      if (bannerFile) {
        const fd = new FormData();
        fd.append("file", bannerFile);
        const up = await api.post("/store-settings/banner", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setSettings(up.data);
        setBannerFile(null);
      } else {
        // O backend normaliza o slug e pode devolver outro (com sufixo).
        // Reexibimos o que ele retornou, nao o que foi digitado.
        setSettings(data);
      }

      toast.success("Loja atualizada!");
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  // ---------- categorias ----------
  const openCatNew = () => {
    setCatForm({ name: "", order: categories.length });
    setCatEditing(null);
    setCatModal(true);
  };

  const openCatEdit = (c) => {
    setCatForm({ name: c.name, order: c.order ?? 0 });
    setCatEditing(c.id);
    setCatModal(true);
  };

  const salvarCat = async () => {
    if (!catForm.name.trim()) {
      toast.error("Informe o nome da categoria.");
      return;
    }
    try {
      if (catEditing) {
        await api.put(`/product-categories/${catEditing}`, {
          name: catForm.name.trim(),
          order: Number(catForm.order) || 0,
        });
      } else {
        await api.post("/product-categories", {
          name: catForm.name.trim(),
          order: Number(catForm.order) || 0,
        });
      }
      toast.success("Categoria salva!");
      setCatModal(false);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const excluirCat = async () => {
    if (!catDeleting) return;
    try {
      await api.delete(`/product-categories/${catDeleting.id}`);
      toast.success("Categoria excluída!");
      load();
    } catch (err) {
      toastError(err);
    }
    setCatDeleting(null);
  };

  if (loading) {
    return (
      <MainContainer>
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <ConfirmationModal
        title={catDeleting ? `Excluir a categoria "${catDeleting.name}"?` : ""}
        open={!!catDeleting}
        onClose={() => setCatDeleting(null)}
        onConfirm={excluirCat}
      >
        Os produtos desta categoria não serão excluídos — eles passam a aparecer
        em "Outros" na loja.
      </ConfirmationModal>

      <Box sx={{ p: 2 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1, mb: 2 }}
        >
          <Storefront /> Loja Online
        </Typography>

        {/* ---------- LINK + ATIVACAO ---------- */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <Chip
              label={settings?.enabled ? "Loja ativa" : "Loja desativada"}
              color={settings?.enabled ? "success" : "default"}
              size="small"
              sx={{ fontWeight: 700 }}
            />
            <FormControlLabel
              control={
                <Switch checked={!!settings?.enabled} onChange={change("enabled")} />
              }
              label="Ativar loja pública"
            />
          </Box>

          {settings?.enabled ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Sua loja está <strong>pública na internet</strong>: qualquer pessoa com
              o link consegue ver seus produtos e enviar pedidos pelo WhatsApp.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              A loja está desativada — o link não abre para ninguém. Ative quando
              estiver pronto para divulgar.
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label="Link da sua loja"
            value={lojaUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copiar link">
                    <IconButton onClick={copiar} size="small">
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Abrir loja">
                    <IconButton onClick={() => window.open(lojaUrl, "_blank")} size="small">
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* ---------- APARENCIA ---------- */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Aparência e informações</Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome exibido na loja"
                fullWidth
                size="small"
                value={settings?.title || ""}
                onChange={change("title")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Endereço (slug)"
                fullWidth
                size="small"
                value={settings?.slug || ""}
                onChange={change("slug")}
                helperText="Só letras, números e hífen. Se já estiver em uso, um número é somado."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Descrição da loja"
                fullWidth
                size="small"
                multiline
                rows={3}
                value={settings?.description || ""}
                onChange={change("description")}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Cor principal"
                type="color"
                fullWidth
                size="small"
                value={settings?.primaryColor || "#1B7A5A"}
                onChange={change("primaryColor")}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button component="label" variant="outlined" size="small" startIcon={<PhotoCamera />}>
                  Banner
                  <input hidden accept="image/png,image/jpeg,image/webp,image/gif" type="file" onChange={pickBanner} />
                </Button>
                {bannerPreview && (
                  <Box
                    component="img"
                    src={bannerPreview}
                    alt="banner"
                    sx={{ height: 46, borderRadius: 1, objectFit: "cover" }}
                  />
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={!!settings?.isOpen} onChange={change("isOpen")} />}
                label="Loja aberta agora"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch checked={!!settings?.deliveryEnabled} onChange={change("deliveryEnabled")} />
                }
                label="Fazemos entrega"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Instagram (link)"
                fullWidth
                size="small"
                value={settings?.instagramUrl || ""}
                onChange={change("instagramUrl")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Facebook (link)"
                fullWidth
                size="small"
                value={settings?.facebookUrl || ""}
                onChange={change("facebookUrl")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="YouTube (link)"
                fullWidth
                size="small"
                value={settings?.youtubeUrl || ""}
                onChange={change("youtubeUrl")}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={salvar}
              disabled={saving}
            >
              Salvar
            </Button>
          </Box>
        </Paper>

        {/* ---------- CATEGORIAS ---------- */}
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700 }}>Categorias</Typography>
            <Button size="small" variant="contained" startIcon={<Add />} onClick={openCatNew}>
              Nova categoria
            </Button>
          </Box>

          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1 }}>
            As categorias viram seções na loja, na ordem definida aqui. Produtos sem
            categoria aparecem em "Outros".
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell align="center">Ordem</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>
                    Nenhuma categoria cadastrada.
                  </TableCell>
                </TableRow>
              )}
              {categories.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                  <TableCell align="center">{c.order ?? 0}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openCatEdit(c)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setCatDeleting(c)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* ---------- MODAL CATEGORIA ---------- */}
      <Dialog open={catModal} onClose={() => setCatModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {catEditing ? "Editar categoria" : "Nova categoria"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nome"
            fullWidth
            size="small"
            autoFocus
            value={catForm.name}
            onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Ordem"
            type="number"
            fullWidth
            size="small"
            value={catForm.order}
            onChange={(e) => setCatForm((f) => ({ ...f, order: e.target.value }))}
            helperText="Menor número aparece primeiro na loja"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatModal(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarCat}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default LojaConfig;
